/*
 * Auto Timetable Generator - Integration Module
 * Connects the auto-generator with the existing timetable system
 */

import { generateAutoTimetable, analyzeConflicts, getClashingTeachersForLab } from './auto-timetable-generator.js';

/**
 * Normalize slot strings so that separators like "/" become "+" and casing is consistent
 */
function normalizeSlotString(slotValue) {
    if (slotValue === null || slotValue === undefined) {
        return '';
    }

    if (Array.isArray(slotValue)) {
        return slotValue
            .map((value) => normalizeSlotString(value))
            .filter(Boolean)
            .join('+');
    }

    let normalized = slotValue.toString().trim();

    if (!normalized) {
        return '';
    }

    const upper = normalized.toUpperCase();
    if (upper === 'NIL') {
        return 'NIL';
    }

    normalized = upper.replace(/\s+/g, '');
    normalized = normalized.replace(/[\/]/g, '+');
    normalized = normalized.replace(/\++/g, '+');
    normalized = normalized.replace(/^\+|\+$/g, '');

    return normalized;
}

/**
 * Get campus schema based on current campus
 */
function getCampusSchema() {
    try {
        if (window.campus === 'Chennai') {
            return require('../schemas/chennai.json');
        } else {
            return require('../schemas/vellore.json');
        }
    } catch (error) {
        console.error('Error loading schema:', error);
        return null;
    }
}

/**
 * Get all available course data based on current campus
 * INCLUDES dynamically added custom teachers from user input
 */
function getAllCourseData() {
    let staticData = [];
    
    // Load static JSON data
    try {
        if (window.campus === 'Chennai') {
            staticData = require('../data/all_data_chennai.json');
        } else {
            staticData = require('../data/all_data_vellore.json');
        }
    } catch (error) {
        console.error('Error loading course data:', error);
    }
    
    // Get dynamic/custom data from timetableStoragePref
    const dynamicData = [];
    
    console.log('[getAllCourseData] Checking for custom data...');
    console.log('[getAllCourseData] window.timetableStoragePref exists?', !!window.timetableStoragePref);
    console.log('[getAllCourseData] window.activeTable exists?', !!window.activeTable);
    console.log('[getAllCourseData] window.activeTable?.id', window.activeTable?.id);
    
    // Also try reading directly from activeTable.subject as a fallback
    const sourceSubjects = {};
    if (window.activeTable && window.activeTable.subject) {
        sourceSubjects.direct = window.activeTable.subject;
        console.log('[getAllCourseData] ✅ Found activeTable.subject directly with', Object.keys(window.activeTable.subject).length, 'subjects');
    }
    
    if (window.timetableStoragePref && window.activeTable && window.activeTable.id !== undefined) {
        const activeTableData = window.timetableStoragePref[window.activeTable.id];
        console.log('[getAllCourseData] activeTableData from storage?', !!activeTableData);
        
        if (activeTableData && activeTableData.subject) {
            sourceSubjects.storage = activeTableData.subject;
            console.log('[getAllCourseData] ✅ Found activeTableData.subject in storage with', Object.keys(activeTableData.subject).length, 'subjects');
        }
    }
    
    // Use direct activeTable.subject if available (most up-to-date), otherwise try storage
    const subjectsToProcess = sourceSubjects.direct || sourceSubjects.storage;
    
    if (subjectsToProcess) {
        console.log('[getAllCourseData] Processing', Object.keys(subjectsToProcess).length, 'subjects from', sourceSubjects.direct ? 'activeTable.subject' : 'storage');
        let pushedCount = 0;
        // Iterate through each course in subject
        for (const courseName in subjectsToProcess) {
                const subject = subjectsToProcess[courseName];

                // Check if this is a lab course (same logic as getCoursesFromPanel)
                const isLab = /lab|Lab|LAB/i.test(courseName) && !/lab\s*\d/i.test(courseName);

                // Parse course code from courseName (format: "CSE1001 - Problem Solving")
                // Use same logic as getCoursesFromPanel() for consistency
                let courseCode, courseTitle;
                const match = courseName.match(/^([A-Z]{2,5}\d{3,5}[A-Z]?[LP]?)\s*-?\s*(.*)$/);

                if (match) {
                    // Standard format with course code prefix
                    courseCode = match[1].trim();
                    courseTitle = (match[2] || '').trim() || courseName;
                } else {
                    // Fallback: use course name as code (same as getCoursesFromPanel)
                    // This handles courses like "Bio Signal" without proper code prefixes
                    const parts = courseName.split(' - ');
                    courseCode = (parts[0] || courseName).trim();
                    courseTitle = (parts.slice(1).join(' - ') || '').trim();
                }

                // For lab courses, append "-Lab" to match the unique code format from getCoursesFromPanel
                const uniqueCode = isLab ? `${courseCode}-Lab` : courseCode;

                // Get all teachers for this course
                if (subject.teacher && Object.keys(subject.teacher).length > 0) {
                    for (const teacherName in subject.teacher) {
                        const teacherData = subject.teacher[teacherName];

                        // Normalize teacher and slots
                        const trimmedTeacherName = (teacherName || '').toString().trim();
                        const rawSlots = (teacherData && teacherData.slots) ? teacherData.slots : '';
                        const normalizedSlots = normalizeSlotString(rawSlots);
                        const venue = (teacherData && teacherData.venue) ? teacherData.venue.toString().trim() : '';

                        // Skip if no slots (empty/undefined) or normalized to NIL/empty
                        if (!normalizedSlots || normalizedSlots === 'NIL') {
                            console.debug('[CustomCourse:skip]', {
                                reason: 'empty_or_nil_slots',
                                code: uniqueCode,
                                originalCode: courseCode,
                                title: courseTitle,
                                teacher: trimmedTeacherName,
                                rawSlots: rawSlots,
                                isLab: isLab
                            });
                            continue;
                        }

                        // Debug each pushed custom option
                        console.debug('[CustomCourse:add]', {
                            code: uniqueCode,
                            originalCode: courseCode,
                            title: courseTitle,
                            teacher: trimmedTeacherName,
                            rawSlots: rawSlots,
                            normalizedSlots: normalizedSlots,
                            venue: venue,
                            credits: subject.credits || 0,
                            isLab: isLab
                        });

                        dynamicData.push({
                            CODE: uniqueCode, // Use unique code (with -Lab suffix for labs)
                            ORIGINAL_CODE: courseCode, // Store original for reference
                            COURSE_NAME: courseName, // Store original course name from activeTable.subject
                            TITLE: courseTitle,
                            FACULTY: trimmedTeacherName,
                            SLOT: normalizedSlots,
                            VENUE: venue,
                            CREDITS: subject.credits || 0,
                            TYPE: isLab ? 'LAB' : 'ETH',
                            source: 'custom', // Mark as custom data
                            isLab: isLab
                        });
                        pushedCount++;
                    }
                }
            }
            if (pushedCount > 0) {
                console.log(`✅ Custom courses collected: ${pushedCount} options from uploaded/selected data`);
            } else {
                console.log('ℹ️ No custom course options collected from uploaded/selected data');
                console.log('[getAllCourseData] Debug: Sample subject keys:', Object.keys(subjectsToProcess).slice(0, 3));
                if (Object.keys(subjectsToProcess).length > 0) {
                    const firstKey = Object.keys(subjectsToProcess)[0];
                    console.log('[getAllCourseData] Debug: Sample subject structure:', {
                        courseName: firstKey,
                        subject: subjectsToProcess[firstKey],
                        hasTeacher: !!subjectsToProcess[firstKey]?.teacher,
                        teacherKeys: subjectsToProcess[firstKey]?.teacher ? Object.keys(subjectsToProcess[firstKey].teacher) : []
                    });
                }
            }
        } else {
            console.log('[getAllCourseData] ❌ No subjects found in either activeTable.subject or storage');
        }
    
    console.log(`📊 Loaded ${staticData.length} static options + ${dynamicData.length} custom options`);
    
    // Combine static and dynamic data
    // Dynamic data takes priority (appears first in search)
    return [...dynamicData, ...staticData];
}

/**
 * Get list of ALL courses (code/title) from dataset regardless of panel state
 */
function getAllCoursesList() {
    try {
        const list = (window.campus === 'Chennai')
            ? require('../data/courses_chennai.json')
            : require('../data/courses_vellore.json');

        // Normalize to {code, title, credits}
        const seen = new Set();
        const courses = [];
        list.forEach(item => {
            const code = item.CODE;
            if (!code || seen.has(code)) return;
            seen.add(code);
            courses.push({ code: code, title: item.TITLE || '', credits: 0 });
        });
        return courses;
    } catch (e) {
        console.error('Error loading courses list:', e);
        return [];
    }
}

/**
 * Extract courses from the subject area (left panel)
 * Uses the same mechanism as fillLeftBoxInCoursePanel() - reads from activeTable.subject
 */
function getCoursesFromPanel() {
    const courses = [];
    const seenCodes = new Set();
    const allCourseNames = [];
    const processedCourses = [];
    const skippedDuplicates = [];
    
    // Read from activeTable.subject - this is the authoritative source
    // Same as what fillLeftBoxInCoursePanel() uses to display courses
    if (window.activeTable && window.activeTable.subject && Object.keys(window.activeTable.subject).length > 0) {
        const subjectKeys = Object.keys(window.activeTable.subject);
        console.log(`[getCoursesFromPanel] Total courses in activeTable.subject: ${subjectKeys.length}`);
        console.log(`[getCoursesFromPanel] All course names in subject:`, subjectKeys);
        
        for (const courseName of subjectKeys) {
            allCourseNames.push(courseName);
            const subject = window.activeTable.subject[courseName];
            
            // Check if this is a lab course (separate schedulable component)
            const isLab = /lab|Lab|LAB/i.test(courseName) && !/lab\s*\d/i.test(courseName); // "lab" but not "lab1", "lab2"
            
            // Parse course name - format: "CSE1001 - Problem Solving" or "CSE1001"
            const match = courseName.match(/^([A-Z]{2,5}\d{3,5}[A-Z]?[LP]?)\s*-?\s*(.*)$/);
            
            if (match) {
                let code = match[1].trim();
                const title = (match[2] || '').trim();
                
                // For lab courses, append "-Lab" to the base code to distinguish from theory
                // This allows both theory and lab to be scheduled separately
                const uniqueCode = isLab ? `${code}-Lab` : code;
                
                // Skip duplicates (but allow theory + lab with same base code)
                if (seenCodes.has(uniqueCode)) {
                    skippedDuplicates.push({ courseName, code, uniqueCode, reason: 'duplicate unique code', isLab });
                    console.log(`[getCoursesFromPanel:skip] Duplicate unique code "${uniqueCode}" for course "${courseName}"`);
                    continue;
                }
                seenCodes.add(uniqueCode);
                
                // Get credits from subject object
                const credits = subject.credits || 0;
                
                processedCourses.push({ courseName, code, uniqueCode, title, credits, method: 'regex match', isLab });
                courses.push({
                    code: uniqueCode, // Use unique code for selection
                    originalCode: code, // Store original code for data lookup
                    courseName: courseName, // Store original course name for lookup
                    title: title,
                    credits: credits,
                    isLab: isLab
                });
                console.log(`[getCoursesFromPanel:add] "${courseName}" → code: "${uniqueCode}"${isLab ? ' (Lab)' : ' (Theory)'}, original: "${code}", title: "${title}", credits: ${credits}`);
            } else {
                // If doesn't match pattern, try to use courseName as-is
                let fallbackCode = courseName.split(' - ')[0] || courseName;
                
                // For lab courses without standard code format, also append -Lab
                const uniqueFallbackCode = isLab ? `${fallbackCode}-Lab` : fallbackCode;
                
                if (!seenCodes.has(uniqueFallbackCode)) {
                    seenCodes.add(uniqueFallbackCode);
                    const fallbackTitle = courseName.split(' - ').slice(1).join(' - ') || '';
                    const credits = subject.credits || 0;
                    
                    processedCourses.push({ courseName, code: fallbackCode, uniqueCode: uniqueFallbackCode, title: fallbackTitle, credits, method: 'fallback', isLab });
                    courses.push({
                        code: uniqueFallbackCode,
                        originalCode: fallbackCode,
                        courseName: courseName, // Store original course name for lookup
                        title: fallbackTitle,
                        credits: credits,
                        isLab: isLab
                    });
                    console.log(`[getCoursesFromPanel:add] "${courseName}" → code: "${uniqueFallbackCode}"${isLab ? ' (Lab)' : ' (Theory)'} (fallback), original: "${fallbackCode}", title: "${fallbackTitle}", credits: ${credits}`);
                } else {
                    skippedDuplicates.push({ courseName, code: fallbackCode, uniqueCode: uniqueFallbackCode, reason: 'duplicate unique fallback code', isLab });
                    console.log(`[getCoursesFromPanel:skip] Duplicate unique fallback code "${uniqueFallbackCode}" for course "${courseName}"`);
                }
            }
        }
    } else {
        console.log(`[getCoursesFromPanel] No activeTable.subject found or it's empty`);
    }
    
    // Fallback: Also check activeTable.data for any courses not in subject
    let addedFromData = 0;
    if (window.activeTable && window.activeTable.data && window.activeTable.data.length > 0) {
        console.log(`[getCoursesFromPanel] Checking activeTable.data (${window.activeTable.data.length} entries)`);
        window.activeTable.data.forEach(courseData => {
            if (courseData.courseCode && !seenCodes.has(courseData.courseCode)) {
                seenCodes.add(courseData.courseCode);
                courses.push({
                    code: courseData.courseCode,
                    title: courseData.courseTitle || '',
                    credits: courseData.credits || 0
                });
                addedFromData++;
                console.log(`[getCoursesFromPanel:add] From data: "${courseData.courseCode}" - "${courseData.courseTitle}", credits: ${courseData.credits}`);
            }
        });
    }
    
    console.log(`[getCoursesFromPanel:summary] ========================================`);
    console.log(`[getCoursesFromPanel:summary] Total courses in activeTable.subject: ${allCourseNames.length}`);
    console.log(`[getCoursesFromPanel:summary] Successfully processed: ${processedCourses.length}`);
    console.log(`[getCoursesFromPanel:summary] Skipped (duplicates): ${skippedDuplicates.length}`);
    console.log(`[getCoursesFromPanel:summary] Added from activeTable.data: ${addedFromData}`);
    console.log(`[getCoursesFromPanel:summary] FINAL COUNT: ${courses.length} courses`);
    console.log(`[getCoursesFromPanel:summary] Final course list:`, courses.map(c => `${c.code} (${c.credits} cr)`));
    if (skippedDuplicates.length > 0) {
        console.log(`[getCoursesFromPanel:summary] Skipped duplicates:`, skippedDuplicates.map(d => `${d.courseName} (${d.code})`));
    }
    console.log(`[getCoursesFromPanel:summary] ========================================`);
    
    return courses;
}

/**
 * Render course selection checkboxes in the modal
 */
function renderCourseSelectionInModal() {
    const container = document.getElementById('auto-gen-course-select');
    if (!container) return;

    // Read courses currently loaded in the panel (what user has added)
    const allCourses = getCoursesFromPanel();

    // Persist existing selections
    const selectedSet = new Set(window.autoGenSelectedCourseCodes || []);

    // Clear container
    container.innerHTML = '';

    if (allCourses.length === 0) {
        container.innerHTML = '<small class="text-muted">No courses found for this campus.</small>';
        return;
    }

    // Group into columns for readability
    const list = document.createElement('div');
    list.className = 'row';

    allCourses.forEach((course, idx) => {
        const col = document.createElement('div');
        col.className = 'col-12 col-md-6 mb-1';

        const id = `auto-course-${course.code}-${idx}`;

        const wrapper = document.createElement('div');
        wrapper.className = 'form-check';

        const input = document.createElement('input');
        input.type = 'checkbox';
        input.className = 'form-check-input auto-gen-course-checkbox';
        input.id = id;
        input.value = course.code;
        input.checked = selectedSet.size === 0 ? true : selectedSet.has(course.code);

        const label = document.createElement('label');
        label.className = 'form-check-label';
        label.setAttribute('for', id);
        label.textContent = `${course.code} — ${course.title || ''}`.trim();

        wrapper.appendChild(input);
        wrapper.appendChild(label);
        col.appendChild(wrapper);
        list.appendChild(col);
    });

    container.appendChild(list);
}

/**
 * Render a full snapshot of the currently loaded timetable (subjects, teachers, slots)
 * into the debug panel inside the auto-generate modal
 */
function renderLoadedTimetableDebug() {
    const container = document.getElementById('auto-gen-debug-loaded');
    if (!container) return;

    try {
        if (!window.activeTable || !window.activeTable.subject || Object.keys(window.activeTable.subject).length === 0) {
            container.innerHTML = '<small class="text-muted">No subjects loaded.</small>';
            return;
        }

        const subject = window.activeTable.subject;
        const lines = [];
        const summarize = [];

        lines.push('Loaded Subjects:');
        const keys = Object.keys(subject);
        keys.forEach((courseName, idx) => {
            const subj = subject[courseName] || {};
            const credits = subj.credits ?? '';
            lines.push(`${idx + 1}. ${courseName}${credits !== '' ? ` (${credits} cr)` : ''}`);

            if (subj.teacher && typeof subj.teacher === 'object') {
                const teacherNames = Object.keys(subj.teacher);
                teacherNames.forEach((tName) => {
                    const t = subj.teacher[tName] || {};
                    const rawSlots = t.slots || '';
                    const normalized = normalizeSlotString(rawSlots);
                    const venue = t.venue || '';
                    lines.push(`   - ${tName} | slots: ${normalized || 'NIL'}${venue ? ` | venue: ${venue}` : ''}`);
                    summarize.push({ courseName, teacher: tName, slots: normalized });
                });
            } else {
                lines.push('   - No teachers added');
            }
        });

        // Save a snapshot for quick access if needed
        window.__AUTO_GEN_DEBUG_SNAPSHOT__ = { subjectsCount: keys.length, summary: summarize };

        container.innerHTML = `<pre style="white-space: pre-wrap; margin: 0;">${lines.join('\n')}</pre>`;
        console.log('[AutoGen:Debug] Snapshot', window.__AUTO_GEN_DEBUG_SNAPSHOT__);
    } catch (e) {
        console.error('[AutoGen:Debug] Failed to render snapshot', e);
        container.innerHTML = '<small class="text-danger">Failed to render snapshot.</small>';
    }
}

/**
 * Read selected course codes from the modal (fallback to all if none ticked)
 */
function readSelectedCourseCodes() {
    const checkboxes = document.querySelectorAll('.auto-gen-course-checkbox');
    const selected = [];
    checkboxes.forEach(cb => {
        if (cb.checked) selected.push(cb.value);
    });
    return selected;
}

/**
 * Show result message in the modal
 */
function showResultMessage(message, type = 'info') {
    const resultDiv = document.getElementById('auto-gen-result');
    if (!resultDiv) return;
    
    resultDiv.style.display = 'block';
    
    const alertClass = type === 'error' ? 'alert-danger' : 
                      type === 'success' ? 'alert-success' : 
                      'alert-info';
    
    const icon = type === 'error' ? 'fa-exclamation-circle' : 
                 type === 'success' ? 'fa-check-circle' : 
                 'fa-info-circle';
    
    // Format multiline messages properly
    const formattedMessage = message.replace(/\n/g, '<br>');
    
    resultDiv.innerHTML = `
        <div class="alert ${alertClass}" style="text-align: left; max-height: 400px; overflow-y: auto;">
            <i class="fas ${icon}"></i> 
            <div style="margin-top: 10px; white-space: pre-wrap; font-family: 'Poppins', sans-serif;">
                ${formattedMessage}
            </div>
        </div>
    `;
    
    // Scroll to result
    resultDiv.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
}

/**
 * Clear the current timetable
 */
function clearCurrentTimetable() {
    // Use the existing clearTimetable function if available
    if (typeof window.clearTimetable === 'function') {
        window.clearTimetable();
    }
    
    // Clear course list
    if (typeof window.clearCourseList === 'function') {
        window.clearCourseList();
    }
    
    // Clear active table data
    if (window.activeTable) {
        window.activeTable.data = [];
    }
}

/**
 * Add generated courses to the timetable
 */
function applyGeneratedTimetable(timetableData) {
    // Clear existing timetable first
    clearCurrentTimetable();
    
    // Add each course
    timetableData.forEach((courseData) => {
        // Use existing functions to add courses
        if (typeof window.addCourseToCourseList === 'function') {
            window.addCourseToCourseList(courseData);
        }
        
        if (typeof window.addCourseToTimetable === 'function') {
            window.addCourseToTimetable(courseData);
        }
        
        // Update activeTable
        if (window.activeTable && window.activeTable.data) {
            window.activeTable.data.push(courseData);
        }
    });
    
    // Update storage
    if (typeof window.updateLocalForage === 'function') {
        window.updateLocalForage();
    }
    
    // Check for any remaining clashes (shouldn't be any, but just to be safe)
    if (typeof window.checkSlotClash === 'function') {
        setTimeout(() => {
            window.checkSlotClash();
        }, 100);
    }
}

/**
 * Get user preferences from the modal
 */
function getUserPreferences() {
    return {
        preferMorning: document.getElementById('auto-gen-prefer-morning')?.checked || false,
        preferEvening: document.getElementById('auto-gen-prefer-evening')?.checked || false,
        minimizeGaps: document.getElementById('auto-gen-minimize-gaps')?.checked || true,
        avoidEarlyMorning: document.getElementById('auto-gen-avoid-early')?.checked || false,
        avoidLateEvening: document.getElementById('auto-gen-avoid-late')?.checked || false,
        preferCompactDays: document.getElementById('auto-gen-compact-days')?.checked || true,
        compactLabs: document.getElementById('auto-gen-compact-labs')?.checked || true,
        labsBefore7PM: document.getElementById('auto-gen-labs-before-7')?.checked || false,
        teacherPreferences: window.teacherPreferences || {},
    };
}

/**
 * Main function to trigger auto-generation
 */
function triggerAutoGeneration() {
    // Show loading state
    const button = document.getElementById('confirm-auto-generate-button');
    const originalText = button.innerHTML;
    button.disabled = true;
    button.innerHTML = '<i class="fas fa-spinner fa-spin"></i> Generating...';
    
    // Get courses from the panel (currently loaded courses)
    let courses = getCoursesFromPanel();
    
    // Filter by selected checkboxes (only include checked courses)
    const selectedCodes = readSelectedCourseCodes();
    if (selectedCodes && selectedCodes.length > 0) {
        const set = new Set(selectedCodes);
        courses = courses.filter(c => set.has(c.code));
        window.autoGenSelectedCourseCodes = selectedCodes;
    } else {
        // If no courses checked, use all courses from panel
        window.autoGenSelectedCourseCodes = [];
    }
    
    if (courses.length === 0) {
        showResultMessage('Please add courses to the panel first. Click "+Course" to add courses.', 'error');
        button.disabled = false;
        button.innerHTML = originalText;
        return;
    }
    
    // Get all course data
    const allData = getAllCourseData();
    
    // Get schema
    const schema = getCampusSchema();
    
    // Get user preferences
    const preferences = getUserPreferences();
    
    // Generate timetable
    const result = generateAutoTimetable(courses, allData, preferences, schema);
    
    if (result.success) {
        // Apply the generated timetable
        applyGeneratedTimetable(result.data);
        
        // Show success message (handle partial solutions)
        if (result.partial) {
            const message = `⚠️ Partial Solution Found!\n\n` +
                          `Scheduled: ${result.scheduled}/${result.total} courses\n` +
                          `Unable to schedule: ${result.unscheduled.join(', ')}\n\n` +
                          `💡 Suggestions:\n` +
                          `• Remove unscheduled courses and try again\n` +
                          `• Check if those courses conflict with each other\n` +
                          `• Relax teacher color restrictions for unscheduled courses\n` +
                          `• The scheduled courses have been added to your timetable.`;
            showResultMessage(message, 'error'); // Use error style for warnings
        } else {
            // Complete solution
            showResultMessage(result.message, 'success');
            
            // Close modal after a short delay
            setTimeout(() => {
                const modal = bootstrap.Modal.getInstance(document.getElementById('auto-generate-modal'));
                if (modal) {
                    modal.hide();
                }
            }, 2000);
        }
    } else {
        // Show error message
        showResultMessage(result.error, 'error');
    }
    
    // Restore button state
    button.disabled = false;
    button.innerHTML = originalText;
}

/**
 * Show conflict analysis button
 */
function showConflictAnalysis() {
    if (!window.activeTable || !window.activeTable.data || window.activeTable.data.length === 0) {
        alert('No courses in the timetable to analyze.');
        return;
    }
    
    const conflicts = analyzeConflicts(window.activeTable.data);
    
    if (conflicts.length === 0) {
        alert('✓ No conflicts found! Your timetable is perfect.');
    } else {
        let message = `⚠️ Found ${conflicts.length} conflict(s):\n\n`;
        
        conflicts.forEach((conflict, index) => {
            message += `${index + 1}. ${conflict.course1.code} (${conflict.course1.slots.join('+')}) `;
            message += `conflicts with ${conflict.course2.code} (${conflict.course2.slots.join('+')})\n`;
        });
        
        alert(message);
    }
}

/**
 * Initialize auto-generation event listeners
 */
export function initializeAutoGeneration() {
    // Confirm button in modal
    const confirmButton = document.getElementById('confirm-auto-generate-button');
    if (confirmButton) {
        confirmButton.addEventListener('click', triggerAutoGeneration);
    }
    
    // Reset result message when modal is opened
    const modal = document.getElementById('auto-generate-modal');
    if (modal) {
        modal.addEventListener('show.bs.modal', () => {
            const resultDiv = document.getElementById('auto-gen-result');
            if (resultDiv) {
                resultDiv.style.display = 'none';
                resultDiv.innerHTML = '';
            }
            // Render course selection
            try { renderCourseSelectionInModal(); } catch (e) { console.error(e); }
            // Render full loaded timetable debug snapshot
            try { renderLoadedTimetableDebug(); } catch (e) { console.error(e); }
        });
    }
    
    // Expose conflict analysis function globally
    window.analyzeCurrentTimetable = showConflictAnalysis;
    
    // Expose clash checking for L courses
    window.checkLabClashes = checkLabClashes;
    
    console.log('Auto-generation system initialized successfully!');
}

/**
 * Check and display clashing teachers when an L course is selected
 * @param {String} labSlot - The lab slot that was selected (e.g., "L1", "L31")
 * @param {String} courseCode - The course code
 */
function checkLabClashes(labSlot, courseCode) {
    const allData = getAllCourseData();
    const teacherPrefs = window.teacherPreferences || {};
    
    const clashes = getClashingTeachersForLab(labSlot, courseCode, allData, teacherPrefs);
    
    if (clashes.green.length === 0 && clashes.orange.length === 0) {
        console.log(`No clashing preferred teachers for ${courseCode} with slot ${labSlot}`);
        return null;
    }
    
    // Build clash message
    let message = `⚠️ Lab Slot ${labSlot} Clashes Detected for ${courseCode}\n\n`;
    
    if (clashes.green.length > 0) {
        message += '🟢 GREEN Teachers (Prioritized) that clash:\n';
        clashes.green.forEach(clash => {
            message += `  • ${clash.teacher}\n`;
            message += `    Slots: ${clash.slots.join('+')}\n`;
            message += `    Clashing at: ${clash.clashingSlots.join(', ')}\n\n`;
        });
    }
    
    if (clashes.orange.length > 0) {
        message += '🟠 ORANGE Teachers (Second Option) that clash:\n';
        clashes.orange.forEach(clash => {
            message += `  • ${clash.teacher}\n`;
            message += `    Slots: ${clash.slots.join('+')}\n`;
            message += `    Clashing at: ${clash.clashingSlots.join(', ')}\n\n`;
        });
    }
    
    message += '\n💡 Tip: Consider choosing a different lab slot to avoid conflicts with your preferred teachers!';
    
    return {
        hasClashes: true,
        message: message,
        clashes: clashes
    };
}

// Export the main initialization function
export default initializeAutoGeneration;

